/**
 * Illinois State-Specific Diagnostic Questions
 * All questions reference actual IL regulations, forms, and requirements
 * Regulatory Body: Illinois Department of Public Health (IDPH)
 * Primary Law: Home Health, Home Services, and Home Nursing Agency Licensing Act (210 ILCS 55)
 * Primary Code: 77 Ill. Adm. Code 245
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const ILLINOIS_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'IL-A1',
    itemId: 'A1',
    stateCode: 'IL',
    title: 'IDPH Home Health Agency License Status',
    description: 'Illinois requires all home health agencies to be licensed by IDPH under 77 Ill. Adm. Code 245',
    severity: 'high',
    regulationCode: '245',
    regulationName: 'Licensure',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov/topics-services/health-care-regulation/facilities/home-health',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active license from IDPH?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home health licenses must be renewed annually. Check your license status with IDPH.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'Found on your IDPH license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your IDPH license is current and active per 210 ILCS 55.',
      atRisk: 'Your license expires within 90 days. Illinois requires renewal applications 45 days before expiration. Submit your renewal application immediately.',
      nonCompliant: 'Operating without a valid IDPH license violates 210 ILCS 55 and can result in civil penalties up to $10,000. Contact IDPH immediately at (217) 782-2913.',
      unknown: 'Verify your license status with IDPH at (217) 782-2913.',
    },
    stateNotes: [
      'Home health licenses must be renewed ANNUALLY (not every 2 years like California)',
      'Renewal applications must be submitted 45 days before expiration',
      'Three license types: Home Health Agency, Home Services Agency, Home Nursing Agency',
      'License type must match services provided',
    ],
  },

  {
    id: 'IL-A2',
    itemId: 'A2',
    stateCode: 'IL',
    title: 'License Type Classification (210 ILCS 55)',
    description: 'Illinois distinguishes between Home Health Agency, Home Services Agency, and Home Nursing Agency licenses',
    severity: 'high',
    regulationCode: '210 ILCS 55',
    regulationName: 'License Type',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov/topics-services/health-care-regulation/facilities/home-health',
    questions: [
      {
        id: 'q1',
        question: 'What type of home care license do you hold?',
        inputType: 'select',
        options: [
          { value: 'home_health', label: 'Home Health Agency (skilled nursing and aide services)' },
          { value: 'home_services', label: 'Home Services Agency (non-medical personal care)' },
          { value: 'home_nursing', label: 'Home Nursing Agency (nursing only)' },
          { value: 'unsure', label: 'Not sure' },
        ],
        required: true,
        helpText: 'License type must match the services you provide',
      },
      {
        id: 'q2',
        question: 'Do you provide any skilled nursing services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - personal care only' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'home_health' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'home_services' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'unsure', result: 'AT_RISK' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'home_services' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your license classification matches the services you provide per 210 ILCS 55.',
      atRisk: 'Verify your license classification matches your services. Contact IDPH to confirm.',
      nonCompliant: 'You are providing skilled nursing services with a Home Services Agency license. This violates 210 ILCS 55. You must upgrade to a Home Health Agency license immediately.',
      unknown: 'Verify your license classification at IDPH.',
    },
    stateNotes: [
      'Home Health Agency: Skilled nursing + aide services',
      'Home Services Agency: Non-medical personal care only',
      'Home Nursing Agency: Nursing services only (no aides)',
      'Providing services outside your license scope is a violation',
    ],
  },

  {
    id: 'IL-A3',
    itemId: 'A3',
    stateCode: 'IL',
    title: 'Insurance Requirements (77 Ill. Adm. Code 245.115)',
    description: 'Illinois requires specific insurance coverage for home health agencies',
    severity: 'high',
    regulationCode: '245.115',
    regulationName: 'Insurance',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'What is your general liability insurance per-occurrence coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'Illinois requires minimum $1,000,000 per occurrence',
      },
      {
        id: 'q2',
        question: 'What is your general liability insurance aggregate coverage amount?',
        inputType: 'number',
        required: true,
        helpText: 'Illinois requires minimum $3,000,000 aggregate',
      },
      {
        id: 'q3',
        question: 'Do you maintain professional liability/malpractice insurance?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q4',
        question: 'Do you maintain workers\' compensation insurance covering all employees?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Workers\' compensation is REQUIRED in Illinois',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 1000000 },
        { condition: 'greaterOrEqual', questionId: 'q2', value: 3000000 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 1000000 },
        { condition: 'greaterOrEqual', questionId: 'q2', value: 2000000 },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'lessThan', questionId: 'q1', value: 1000000 },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your insurance coverage meets Illinois 77 Ill. Adm. Code 245.115 requirements.',
      atRisk: 'Your insurance may not meet all Illinois requirements. Review coverage levels with IDPH.',
      nonCompliant: 'Your insurance coverage does not meet Illinois requirements. Missing workers\' compensation or inadequate liability coverage can result in license revocation.',
      unknown: 'Verify your insurance meets 77 Ill. Adm. Code 245.115 requirements.',
    },
    stateNotes: [
      'Illinois requires $1M per occurrence AND $3M aggregate liability',
      'Workers\' compensation is REQUIRED - no exceptions',
      'Professional liability/malpractice insurance required for skilled services',
      'Proof of insurance required with license renewal',
    ],
  },

  {
    id: 'IL-A4',
    itemId: 'A4',
    stateCode: 'IL',
    title: 'Written Policies and Procedures (77 Ill. Adm. Code 245.200)',
    description: 'Illinois requires specific written policies per 77 Ill. Adm. Code 245.200',
    severity: 'medium',
    regulationCode: '245.200',
    regulationName: 'Policies',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following Illinois-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient rights (77 Ill. Adm. Code 245.210)' },
          { value: 'admission', label: 'Admission and discharge criteria' },
          { value: 'personnel', label: 'Personnel policies and job descriptions' },
          { value: 'supervision', label: 'Clinical supervision procedures' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'background', label: 'Background check procedures (HCWBC Act)' },
          { value: 'infection_control', label: 'Infection control program' },
          { value: 'emergency', label: 'Emergency preparedness plan' },
          { value: 'quality', label: 'Quality assurance program' },
          { value: 'records', label: 'Record retention (5 years per 77 Ill. Adm. Code 245.260)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets Illinois 77 Ill. Adm. Code 245.200 requirements.',
      atRisk: 'Your policy manual is missing required sections per 77 Ill. Adm. Code 245.200. IDPH surveyors will review your policies for completeness. Missing policies: {missing}.',
      nonCompliant: 'Multiple required policies are missing per 77 Ill. Adm. Code 245.200. This is a common IDPH survey deficiency. Immediately develop written policies for: {missing}.',
      unknown: 'Review your policies against 77 Ill. Adm. Code 245.200 requirements.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.200 requires comprehensive written policies',
      '210 ILCS 55 authorizes IDPH to require policies as condition of licensure',
      'Policies must be reviewed and updated annually per IDPH guidelines',
      'All staff must acknowledge receipt of policies in writing',
    ],
  },

  {
    id: 'IL-A5',
    itemId: 'A5',
    stateCode: 'IL',
    title: 'Incident and Abuse Reporting Process (320 ILCS 20)',
    description: 'Illinois requires documented incident and abuse reporting procedures per Adult Protective Services Act',
    severity: 'high',
    regulationCode: '320 ILCS 20',
    regulationName: 'Abuse Reporting',
    agencyName: 'Illinois Department on Aging',
    agencyAbbr: 'IDoA',
    agencyPhone: '(866) 800-1409',
    agencyUrl: 'https://ilaging.illinois.gov/protectionadvocacy/abuse.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented incident and abuse reporting process?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, fully documented with APS hotline information available' },
          { value: 'informal', label: 'Yes, but informal / not fully documented' },
          { value: 'no', label: 'No incident reporting process in place' },
        ],
        required: true,
        helpText: '320 ILCS 20 requires reporting of suspected elder/dependent adult abuse',
      },
      {
        id: 'q2',
        question: 'Which types of incidents does your reporting process cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'physical', label: 'Physical abuse' },
          { value: 'neglect', label: 'Neglect' },
          { value: 'financial', label: 'Financial exploitation' },
          { value: 'emotional', label: 'Emotional abuse' },
          { value: 'sexual', label: 'Sexual abuse' },
          { value: 'injury', label: 'Patient injuries during service' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'documented' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'informal' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident and abuse reporting process is documented per 320 ILCS 20.',
      atRisk: 'Incident reporting process exists but may not be fully documented. Ensure APS hotline (866) 800-1409 is available to all staff.',
      nonCompliant: 'No incident reporting process is in place. 320 ILCS 20 requires reporting of suspected abuse. All staff are mandated reporters.',
      unknown: 'Verify your incident reporting procedures meet 320 ILCS 20 requirements.',
    },
    stateNotes: [
      'Illinois Adult Protective Services Hotline: (866) 800-1409',
      'Reports can be made 24/7',
      'All home health workers are mandated reporters',
      'Telephone report required immediately or within 24 hours',
      'Written report may be required within 2 working days',
    ],
  },

  {
    id: 'IL-A6',
    itemId: 'A6',
    stateCode: 'IL',
    title: 'Emergency Preparedness Plan (77 Ill. Adm. Code 245.250)',
    description: 'Illinois requires documented emergency preparedness procedures',
    severity: 'medium',
    regulationCode: '245.250',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'documented', label: 'Yes, comprehensive written plan including severe weather and power outages' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
        helpText: 'Illinois agencies should address tornadoes, severe weather, winter storms, and power outages',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'documented', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is comprehensive and addresses Illinois-specific hazards.',
      atRisk: 'Emergency preparedness plan is incomplete. Include tornado, severe weather, winter storm, and power outage procedures.',
      nonCompliant: 'No emergency preparedness plan is in place. This violates 77 Ill. Adm. Code 245.250.',
      unknown: 'Review your emergency preparedness procedures for Illinois-specific hazards.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.250 requires written emergency preparedness procedures',
      '210 ILCS 55 mandates agencies plan for continuity of care during emergencies',
      'Plans must address Illinois-specific hazards: tornadoes, severe storms, winter weather',
      'Annual review and staff training on emergency procedures required',
    ],
  },

  {
    id: 'IL-A7',
    itemId: 'A7',
    stateCode: 'IL',
    title: 'Quality Assessment Program (77 Ill. Adm. Code 245.240)',
    description: 'Illinois requires ongoing quality assessment and performance improvement',
    severity: 'medium',
    regulationCode: '245.240',
    regulationName: 'Quality Assessment',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct and document quality assurance reviews?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, regular documented reviews (at least quarterly)' },
          { value: 'occasional', label: 'Occasional reviews, not consistently documented' },
          { value: 'no', label: 'No quality assurance reviews' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'regular', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'occasional', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality assessment program meets 77 Ill. Adm. Code 245.240 requirements.',
      atRisk: 'Quality assurance reviews are not conducted consistently. Implement regular documented reviews.',
      nonCompliant: 'No quality assessment program exists. This violates 77 Ill. Adm. Code 245.240.',
      unknown: 'Review quality assessment requirements under 77 Ill. Adm. Code 245.240.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.240 requires ongoing quality assessment program',
      '210 ILCS 55 authorizes IDPH to enforce quality standards',
      'Documentation of QA activities must be maintained for IDPH review',
      'Quarterly reviews recommended to identify and address issues proactively',
    ],
  },

  {
    id: 'IL-A8',
    itemId: 'A8',
    stateCode: 'IL',
    title: 'Record Retention (77 Ill. Adm. Code 245.260)',
    description: 'Illinois requires 5 years record retention',
    severity: 'medium',
    regulationCode: '245.260',
    regulationName: 'Record Retention',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer (meets IL requirement)' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Illinois requires retention for at least 5 years',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets Illinois\'s 5-year requirement under 77 Ill. Adm. Code 245.260.',
      atRisk: 'Illinois requires 5-year record retention per 77 Ill. Adm. Code 245.260. Update your policy to ensure compliance.',
      nonCompliant: 'Your record retention does not meet Illinois\'s 5-year requirement. Immediately update your retention policy.',
      unknown: 'Review record retention requirements under 77 Ill. Adm. Code 245.260.',
    },
    stateNotes: [
      'Illinois requires 5-year retention (California requires 7 years)',
      'Records for minors may require longer retention',
      'Both patient and employee records subject to requirement',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'IL-B1',
    itemId: 'B1',
    stateCode: 'IL',
    title: 'Personnel File Requirements (77 Ill. Adm. Code 245.220)',
    description: 'Illinois requires specific documents in each staff personnel file',
    severity: 'high',
    regulationCode: '245.220',
    regulationName: 'Personnel Files',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY direct care worker\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID' },
          { value: 'credentials', label: 'Credentials verification (CNA certificate, nursing license)' },
          { value: 'hcwbc', label: 'Health Care Worker Background Check (HCWBC) results' },
          { value: 'registry', label: 'Health Care Worker Registry (HCWR) verification' },
          { value: 'references', label: 'Reference checks' },
          { value: 'physical', label: 'Physical exam/health assessment' },
          { value: 'tb', label: 'TB test results' },
          { value: 'training', label: 'Training and competency documentation' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Illinois-required documentation per 77 Ill. Adm. Code 245.220.',
      atRisk: 'Some personnel files are missing required documents. Illinois requires: HCWBC results, HCWR verification, TB results, and training records. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation required by 77 Ill. Adm. Code 245.220. IDPH surveyors will cite incomplete files. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against 77 Ill. Adm. Code 245.220 requirements.',
    },
    stateNotes: [
      'HCWBC is Illinois-specific (equivalent to California Live Scan)',
      'HCWR verification required for CNAs and home health aides',
      'All documents must be in place BEFORE patient contact',
    ],
  },

  {
    id: 'IL-B2',
    itemId: 'B2',
    stateCode: 'IL',
    title: 'Home Health Aide Qualifications (77 Ill. Adm. Code 245.220)',
    description: 'Illinois requires specific qualifications for home health aides',
    severity: 'high',
    regulationCode: '245.220',
    regulationName: 'Aide Qualifications',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home health aides meet Illinois qualification requirements?',
        inputType: 'checklist',
        options: [
          { value: 'age', label: 'At least 18 years old' },
          { value: 'education', label: 'High school diploma or equivalent' },
          { value: 'training', label: 'Completed state-approved training program (40+ hours)' },
          { value: 'competency', label: 'Passed competency evaluation' },
          { value: 'registry', label: 'Listed on Health Care Worker Registry (HCWR)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All home health aides meet Illinois qualification requirements per 77 Ill. Adm. Code 245.220.',
      atRisk: 'Some aides may not meet all qualification requirements. Review credentials immediately.',
      nonCompliant: 'Aides are working who do not meet Illinois requirements. This violates 77 Ill. Adm. Code 245.220.',
      unknown: 'Verify all aide qualifications against requirements.',
    },
    stateNotes: [
      'Illinois requires 40+ hours training for home health aides',
      'CNAs must maintain current certification and HCWR listing',
      'Competency evaluation must be documented',
    ],
  },

  {
    id: 'IL-B3',
    itemId: 'B3',
    stateCode: 'IL',
    title: 'Professional License Verification',
    description: 'Illinois requires verification of all professional licenses through IDFPR',
    severity: 'high',
    regulationCode: '225 ILCS 65',
    regulationName: 'License Verification',
    agencyName: 'Illinois Department of Financial and Professional Regulation',
    agencyAbbr: 'IDFPR',
    agencyPhone: '(888) 473-4858',
    agencyUrl: 'https://idfpr.illinois.gov/profs/nursing.asp',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your nurses have current, active Illinois nursing licenses?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all nurses verified and current' },
          { value: 'most', label: 'Most are verified, some pending' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you verify licenses through the IDFPR online system BEFORE hire and track renewals?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'hire_only' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All professional licenses verified through IDFPR with ongoing tracking.',
      atRisk: 'License verification or tracking is inconsistent. Verify all licenses and implement renewal tracking.',
      nonCompliant: 'Nurses working without verified Illinois licenses violates the Nurse Practice Act (225 ILCS 65). Verify all licenses immediately with IDFPR.',
      unknown: 'Verify all professional licenses with IDFPR.',
    },
    stateNotes: [
      'IDFPR is Illinois licensing authority (not IDPH)',
      'Free online license verification available at idfpr.illinois.gov',
      'RN and LPN licenses renew every 2 years',
      'CNAs verified through Health Care Worker Registry (HCWR)',
    ],
  },

  {
    id: 'IL-B4',
    itemId: 'B4',
    stateCode: 'IL',
    title: 'HIPAA/Confidentiality Agreement Signed',
    description: 'Illinois requires signed HIPAA and confidentiality agreements',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'Confidentiality',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care workers have a signed HIPAA/confidentiality agreement on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have signed agreements' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not consistently maintained' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff have signed HIPAA/confidentiality agreements on file.',
      atRisk: 'Some staff are missing signed HIPAA/confidentiality agreements. This creates significant privacy risk.',
      nonCompliant: 'Personnel files are missing required HIPAA/confidentiality agreements.',
      unknown: 'Verify all staff have signed HIPAA/confidentiality agreements on file.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.220 requires confidentiality agreements in personnel files',
      '210 ILCS 55 reinforces patient privacy protections under state law',
      'Federal HIPAA (45 CFR 164) requirements also apply to Illinois agencies',
      'Agreements must be signed before employee has access to patient information',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'IL-C1',
    itemId: 'C1',
    stateCode: 'IL',
    title: 'Health Care Worker Background Check (225 ILCS 46)',
    description: 'Illinois requires fingerprint-based background checks under the Health Care Worker Background Check Act',
    severity: 'high',
    regulationCode: '225 ILCS 46',
    regulationName: 'Background Checks',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov/topics-services/health-care-regulation/background-check',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed direct care staff, which of the following do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hcwbc', label: 'Health Care Worker Background Check (HCWBC) results' },
          { value: 'fingerprint', label: 'Fingerprint-based criminal history check (ISP/FBI)' },
          { value: 'sex_offender', label: 'Sex Offender Registry check' },
          { value: 'exclusion', label: 'OIG/Medicaid Exclusion List verification' },
          { value: 'registry', label: 'Health Care Worker Registry (HCWR) verification' },
        ],
        required: true,
        helpText: 'Illinois requires HCWBC completed within 10 days of hire - no client contact until cleared',
      },
      {
        id: 'q2',
        question: 'Do any employees begin working with patients BEFORE their HCWBC is completed?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if employees start before clearance - this is a DIRECT VIOLATION
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if missing required background checks
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
      // COMPLIANT only if 4+ checks AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      // AT_RISK if only 3 checks (but no early starts)
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'HCWBC documentation is complete for all staff per 225 ILCS 46.',
      atRisk: 'Background check documentation may be incomplete. Illinois requires HCWBC BEFORE any patient contact.',
      nonCompliant: 'VIOLATION: Staff are working before HCWBC clearance OR required checks are missing. Per 225 ILCS 46, NO patient contact is allowed until clearance received. This violates Illinois law and can result in fines up to $10,000. Immediately remove uncleared staff from patient assignments.',
      unknown: 'Audit all personnel files for HCWBC documentation per 225 ILCS 46.',
    },
    stateNotes: [
      'HCWBC includes BOTH Illinois State Police and FBI fingerprint checks',
      'Background check must be initiated within 10 days of hire',
      'NO patient contact allowed until clearance received',
      'HCWR provides "rap back" notifications for subsequent arrests',
      'Certain convictions result in permanent prohibition from employment',
    ],
  },

  {
    id: 'IL-C2',
    itemId: 'C2',
    stateCode: 'IL',
    title: 'Health Care Worker Registry (HCWR) Verification',
    description: 'Must verify staff against the Illinois Health Care Worker Registry',
    severity: 'high',
    regulationCode: '955',
    regulationName: 'HCWR Verification',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov/topics-services/health-care-regulation/hcwr',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all staff against the Illinois Health Care Worker Registry BEFORE hiring?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, 100% verified before hire' },
          { value: 'usually', label: 'Usually, but not always' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
        helpText: 'HCWR tracks CNAs, home health aides, and findings of abuse/neglect/theft',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff verified against Illinois HCWR per 77 Ill. Adm. Code 955.',
      atRisk: 'HCWR verification is inconsistent. ALWAYS verify before hiring.',
      nonCompliant: 'Failure to verify HCWR status before hiring violates 77 Ill. Adm. Code 955. Verify all current and prospective staff.',
      unknown: 'Verify all staff against the Illinois HCWR.',
    },
    stateNotes: [
      'HCWR contains abuse/neglect/theft findings - employment prohibition',
      'CNA certification status verified through HCWR',
      'Free online verification available',
      'Must verify BEFORE hire, not after',
    ],
  },

  {
    id: 'IL-C3',
    itemId: 'C3',
    stateCode: 'IL',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal exclusion list checks required for all staff',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusions',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify ALL employees against the OIG and SAM exclusion lists?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, at hire AND monthly thereafter' },
          { value: 'hire', label: 'Yes, at hire only' },
          { value: 'no', label: 'No / not consistently verified' },
        ],
        required: true,
        helpText: 'CMS requires monthly exclusion list checks for Medicaid providers',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion list verification conducted at hire and monthly.',
      atRisk: 'Exclusion list verification at hire only is insufficient. Implement monthly checks.',
      nonCompliant: 'No OIG/SAM exclusion list verification conducted. This creates significant Medicaid compliance risk.',
      unknown: 'Implement OIG/SAM exclusion list verification process.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245 requires verification against federal exclusion lists',
      '210 ILCS 55 prohibits employment of excluded individuals in licensed agencies',
      'OIG recommends monthly verification per 42 CFR 1001',
      'Employing excluded individuals can result in civil monetary penalties and program exclusion',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'IL-D1',
    itemId: 'D1',
    stateCode: 'IL',
    title: 'TB Screening (77 Ill. Adm. Code 245.220)',
    description: 'Illinois requires TB screening for all patient care staff',
    severity: 'high',
    regulationCode: '245.220',
    regulationName: 'TB Screening',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your patient care staff have current TB screening documentation on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have TB screening documentation' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / Not tracking this' },
        ],
        required: true,
        helpText: 'TB screening must be completed before patient contact',
      },
      {
        id: 'q2',
        question: 'How do you track TB screening renewals?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated system with reminders' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking system' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'none' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is current for all staff per 77 Ill. Adm. Code 245.220.',
      atRisk: 'Some TB screenings may be missing or expired. Review all staff files.',
      nonCompliant: 'Staff are working without TB screening. This violates 77 Ill. Adm. Code 245.220. Immediately obtain TB screening for all staff.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'TB screening required BEFORE patient contact',
      'Annual screening may be required based on risk assessment',
      'Positive TB tests require additional medical clearance',
    ],
  },

  {
    id: 'IL-D2',
    itemId: 'D2',
    stateCode: 'IL',
    title: 'Infection Control Program (77 Ill. Adm. Code 245.220)',
    description: 'Illinois requires comprehensive infection control policies',
    severity: 'high',
    regulationCode: '245.220',
    regulationName: 'Infection Control',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written infection control program that includes? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'ppe', label: 'Personal protective equipment (PPE) guidelines' },
          { value: 'bloodborne', label: 'Bloodborne pathogen exposure procedures' },
          { value: 'outbreak', label: 'Disease outbreak response plan' },
          { value: 'training', label: 'Infection control training for all staff' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets 77 Ill. Adm. Code 245.220 requirements.',
      atRisk: 'Infection control program is incomplete. Update policies to include all required elements.',
      nonCompliant: 'Infection control program does not meet requirements. Immediately develop comprehensive infection control policies.',
      unknown: 'Review infection control program against 77 Ill. Adm. Code 245.220 requirements.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.220 mandates comprehensive infection control program',
      '210 ILCS 55 requires agencies to protect patient health and safety',
      'Program must include hand hygiene, PPE, bloodborne pathogen protocols',
      'Annual infection control training required for all patient care staff',
    ],
  },

  {
    id: 'IL-D3',
    itemId: 'D3',
    stateCode: 'IL',
    title: 'Hepatitis B Vaccination (OSHA)',
    description: 'OSHA requires Hepatitis B vaccination offer for at-risk workers',
    severity: 'medium',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'Hepatitis B',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://osha.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you offer Hepatitis B vaccination to all staff with occupational exposure risk?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, offered to all at-risk staff' },
          { value: 'some', label: 'Offered to some staff' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document vaccination or signed declination for each employee?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Hepatitis B vaccination program meets OSHA requirements.',
      atRisk: 'Vaccination is not offered to all at-risk staff. Review OSHA compliance.',
      nonCompliant: 'Failure to offer Hepatitis B vaccination violates OSHA. Implement immediately.',
      unknown: 'Review Hepatitis B vaccination program.',
    },
    stateNotes: [
      '29 CFR 1910.1030 requires Hepatitis B vaccination offer to at-risk employees',
      '77 Ill. Adm. Code 245 incorporates federal OSHA requirements for Illinois agencies',
      'Vaccination must be offered at no cost within 10 days of assignment',
      'Written declination required if employee refuses vaccination',
    ],
  },

  {
    id: 'IL-D4',
    itemId: 'D4',
    stateCode: 'IL',
    title: 'PPE Availability and Training',
    description: 'Illinois requires PPE availability and training for staff',
    severity: 'high',
    regulationCode: '245.220',
    regulationName: 'PPE',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Is appropriate PPE available to ALL direct care staff?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, PPE is readily available to all staff' },
          { value: 'some', label: 'Available but not consistently' },
          { value: 'no', label: 'PPE is not provided' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff receive training on proper PPE use?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'PPE is available to all staff with proper training.',
      atRisk: 'PPE availability or training is inconsistent. Ensure all staff have access and training.',
      nonCompliant: 'PPE is not provided to staff. This violates OSHA and IDPH requirements.',
      unknown: 'Verify PPE availability and training for all staff.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.220 requires appropriate PPE for all patient care activities',
      '210 ILCS 55 mandates agencies provide safe working conditions',
      'PPE training must be documented in personnel files per OSHA 29 CFR 1910.132',
      'Agencies must provide PPE at no cost to employees',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'IL-E1',
    itemId: 'E1',
    stateCode: 'IL',
    title: 'Initial Training Requirements (77 Ill. Adm. Code 245.220)',
    description: 'Illinois requires comprehensive initial training before patient contact',
    severity: 'high',
    regulationCode: '245.220',
    regulationName: 'Initial Training',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'How many hours of initial training do ALL new home health aides complete BEFORE patient contact?',
        inputType: 'number',
        required: true,
        helpText: 'Illinois requires minimum 40 hours for home health aides',
      },
      {
        id: 'q2',
        question: 'Does initial training include the following required components? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'orientation', label: 'Agency orientation and policies' },
          { value: 'skills', label: 'Basic care skills (bathing, transferring, vital signs)' },
          { value: 'safety', label: 'Safety and emergency procedures' },
          { value: 'infection', label: 'Infection control' },
          { value: 'abuse', label: 'Abuse/neglect recognition and reporting' },
          { value: 'documentation', label: 'Documentation requirements' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 40 },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 20 },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'lessThan', questionId: 'q1', value: 20, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Initial training program meets Illinois 77 Ill. Adm. Code 245.220 requirements (40+ hours for home health aides).',
      atRisk: 'Initial training may not meet Illinois requirements. Home health aides require 40+ hours training BEFORE patient contact.',
      nonCompliant: 'Staff are providing services without required initial training. This violates 77 Ill. Adm. Code 245.220. Immediately complete training.',
      unknown: 'Review initial training program against 77 Ill. Adm. Code 245.220 requirements.',
    },
    stateNotes: [
      'Illinois requires 40+ hours for home health aides (vs. California\'s 5 hours)',
      'Training must be completed BEFORE patient contact',
      'Must include orientation, skills, safety, infection control',
      'Competency evaluation required after training',
    ],
  },

  {
    id: 'IL-E2',
    itemId: 'E2',
    stateCode: 'IL',
    title: 'Annual In-Service Training (77 Ill. Adm. Code 245.220)',
    description: 'Illinois requires 12 hours of annual in-service training',
    severity: 'high',
    regulationCode: '245.220',
    regulationName: 'Annual Training',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'How many hours of annual in-service training do ALL direct care workers complete?',
        inputType: 'number',
        required: true,
        helpText: 'Illinois requires minimum 12 hours of annual training',
      },
      {
        id: 'q2',
        question: 'Which required topics does your annual training cover? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'infection', label: 'Infection control' },
          { value: 'safety', label: 'Patient safety' },
          { value: 'abuse', label: 'Abuse/neglect recognition and reporting' },
          { value: 'emergency', label: 'Emergency procedures' },
          { value: 'hipaa', label: 'HIPAA and patient privacy' },
          { value: 'skills', label: 'Clinical skills competency' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 12 },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'greaterOrEqual', questionId: 'q1', value: 6 },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'lessThan', questionId: 'q1', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual training program meets Illinois 77 Ill. Adm. Code 245.220 requirements (12 hours).',
      atRisk: 'Some staff may be behind on annual training. Illinois requires 12 hours annually. Review training records.',
      nonCompliant: 'Annual training does not meet Illinois requirements. Implement a 12-hour annual training program immediately.',
      unknown: 'Review annual training program against 77 Ill. Adm. Code 245.220 requirements.',
    },
    stateNotes: [
      'Illinois requires 12 hours annual (vs. California\'s 5 hours)',
      'Must include infection control, safety, abuse reporting',
      'Training records must be documented in personnel files',
    ],
  },

  {
    id: 'IL-E3',
    itemId: 'E3',
    stateCode: 'IL',
    title: 'Mandated Reporter Training (320 ILCS 20)',
    description: 'All staff are mandated reporters of adult abuse under Illinois law',
    severity: 'high',
    regulationCode: '320 ILCS 20',
    regulationName: 'Mandated Reporters',
    agencyName: 'Illinois Department on Aging',
    agencyAbbr: 'IDoA',
    agencyPhone: '(866) 800-1409',
    agencyUrl: 'https://ilaging.illinois.gov/protectionadvocacy/abuse.html',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care workers receive training on mandated reporter responsibilities?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are trained as mandated reporters' },
          { value: 'most', label: 'Most staff are trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report suspected abuse to the APS hotline (866) 800-1409?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on mandated reporter duties per 320 ILCS 20.',
      atRisk: 'Some staff may lack mandated reporter training. All staff are mandated reporters under Illinois law.',
      nonCompliant: 'Failure to train staff on mandated reporter duties violates 320 ILCS 20. Staff who fail to report suspected abuse face criminal penalties.',
      unknown: 'Verify all staff have received mandated reporter training.',
    },
    stateNotes: [
      'Illinois Adult Protective Services Hotline: (866) 800-1409',
      'Available 24/7',
      'All home health workers are mandated reporters',
      'Failure to report is a Class A misdemeanor',
    ],
  },

  {
    id: 'IL-E4',
    itemId: 'E4',
    stateCode: 'IL',
    title: 'Competency Evaluation',
    description: 'Staff must demonstrate competency in required skills',
    severity: 'high',
    regulationCode: '245.220',
    regulationName: 'Competency',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care staff have documented competency evaluations on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have completed competency evaluations' },
          { value: 'most', label: 'Most staff have them' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are competency evaluations performed by a licensed professional (RN)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always by RN or appropriate professional' },
          { value: 'no', label: 'No / not always' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff have documented competency evaluations per 77 Ill. Adm. Code 245.220.',
      atRisk: 'Some competency evaluations may be missing or not performed by licensed professional.',
      nonCompliant: 'Staff are working without documented competency evaluations. This violates 77 Ill. Adm. Code 245.220.',
      unknown: 'Verify all staff have competency evaluations.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.220 requires documented competency evaluation for all direct care staff',
      '210 ILCS 55 mandates agencies ensure staff are qualified to perform assigned duties',
      'Competency must be evaluated by RN or appropriate licensed professional',
      'Re-evaluation required when new skills are assigned or performance concerns arise',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'IL-F1',
    itemId: 'F1',
    stateCode: 'IL',
    title: 'Plan of Care (77 Ill. Adm. Code 245.230)',
    description: 'Written plan of care required for each patient before services begin',
    severity: 'high',
    regulationCode: '245.230',
    regulationName: 'Plan of Care',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all Illinois-required elements?',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Patient diagnosis and medical history' },
          { value: 'services', label: 'Specific services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of services' },
          { value: 'goals', label: 'Patient-specific goals' },
          { value: 'physician', label: 'Physician orders (as applicable)' },
          { value: 'review', label: 'Plan review schedule (at least every 60 days)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care are completed before services begin and include all required elements per 77 Ill. Adm. Code 245.230.',
      atRisk: 'Plans of care may be incomplete or created after services begin. 77 Ill. Adm. Code 245.230 requires written plans before first service.',
      nonCompliant: 'Services are being provided without written plans of care. This violates 77 Ill. Adm. Code 245.230. Immediately create plans for all patients.',
      unknown: 'Review plan of care process against 77 Ill. Adm. Code 245.230 requirements.',
    },
    stateNotes: [
      'Plan of care must be in place BEFORE services begin',
      'Must be reviewed at least every 60 days',
      'Must include physician orders for skilled services',
      'Patient/representative should acknowledge the plan',
    ],
  },

  {
    id: 'IL-F2',
    itemId: 'F2',
    stateCode: 'IL',
    title: 'RN Supervision Requirements (77 Ill. Adm. Code 245.220)',
    description: 'Illinois requires RN supervision of home health aides',
    severity: 'high',
    regulationCode: '245.220',
    regulationName: 'Supervision',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide RN supervision of home health aides as required?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, per regulatory schedule' },
          { value: 'partial', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No supervision program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often does RN supervision occur?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'At least every 14 days' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'every_60', label: 'Every 60 days' },
          { value: 'less', label: 'Less frequently' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      // NON_COMPLIANT: No supervision program
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT: Less frequent than 60 days
      { condition: 'equals', questionId: 'q2', value: 'less', result: 'NON_COMPLIANT' },
      // AT_RISK: Inconsistent supervision
      { condition: 'equals', questionId: 'q1', value: 'partial', result: 'AT_RISK' },
      // AT_RISK: 30 or 60 day frequency
      { condition: 'includes', questionId: 'q2', value: ['every_30', 'every_60'], result: 'AT_RISK' },
      // COMPLIANT: Regular supervision at least every 14 days
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'every_14' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision schedule meets 77 Ill. Adm. Code 245.220 requirements.',
      atRisk: 'RN supervision may be inconsistent or not frequent enough. Review schedule to ensure compliance.',
      nonCompliant: 'No RN supervision program or inadequate frequency. This violates 77 Ill. Adm. Code 245.220.',
      unknown: 'Review supervision requirements per 77 Ill. Adm. Code 245.220.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.220 requires RN supervision of home health aides',
      '210 ILCS 55 mandates appropriate clinical oversight of patient care',
      'Supervision frequency depends on patient acuity and service type',
      'All supervision visits must be documented in patient and personnel records',
    ],
  },

  {
    id: 'IL-F3',
    itemId: 'F3',
    stateCode: 'IL',
    title: 'Patient Rights (77 Ill. Adm. Code 245.210)',
    description: 'Illinois requires patient rights notification and documentation',
    severity: 'high',
    regulationCode: '245.210',
    regulationName: 'Patient Rights',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights to all patients at admission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do patients sign acknowledgment of receiving patient rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights are provided and documented per 77 Ill. Adm. Code 245.210.',
      atRisk: 'Patient rights acknowledgment is not documented. Implement signed acknowledgment.',
      nonCompliant: 'Patient rights are not being provided. This violates 77 Ill. Adm. Code 245.210.',
      unknown: 'Verify patient rights process meets requirements.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.210 specifies required patient rights disclosures',
      '210 ILCS 55 establishes patient rights as condition of licensure',
      'Written rights must be provided before or at start of services',
      'Patient/representative must acknowledge receipt with signature',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION
  // ============================================
  {
    id: 'IL-G1',
    itemId: 'G1',
    stateCode: 'IL',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: '245.260',
    regulationName: 'Document Tracking',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents?',
        inputType: 'checklist',
        options: [
          { value: 'agency_license', label: 'Agency license (annual renewal)' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'workers_comp', label: 'Workers\' compensation' },
          { value: 'nursing_licenses', label: 'Professional nursing licenses' },
          { value: 'cna_certs', label: 'CNA certifications' },
          { value: 'tb', label: 'TB screenings' },
          { value: 'training', label: 'Annual training due dates' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Missing tracking for: {missing}. Implement tracking to prevent compliance lapses.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.260 requires current documentation in personnel files',
      '210 ILCS 55 mandates agencies maintain valid licenses and certifications',
      'IDPH surveyors will verify current status of all required documents',
      'Annual agency license renewal required - track 45 days before expiration',
    ],
  },

  {
    id: 'IL-G2',
    itemId: 'G2',
    stateCode: 'IL',
    title: 'Electronic Visit Verification (EVV)',
    description: 'EVV required for Medicaid-funded personal care services',
    severity: 'high',
    regulationCode: 'IL HFS',
    regulationName: 'EVV',
    agencyName: 'Illinois Department of Healthcare and Family Services',
    agencyAbbr: 'HFS',
    agencyPhone: '(877) 782-5565',
    agencyUrl: 'https://hfs.illinois.gov/medicalproviders/evv.html',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid-funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use the Illinois EVV system (Sandata) for all Medicaid visits?',
        inputType: 'select',
        options: [
          { value: 'yes_compliant', label: 'Yes, fully implemented' },
          { value: 'yes_partial', label: 'Yes, but not for all visits' },
          { value: 'no', label: 'No EVV system' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_compliant', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes_partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented for Medicaid services.',
      atRisk: 'EVV is not used for all Medicaid visits. Federal law requires EVV compliance.',
      nonCompliant: 'Medicaid services require EVV per federal law. Implement EVV immediately or risk Medicaid payment denials.',
      unknown: 'Verify EVV compliance if providing Medicaid services.',
    },
    stateNotes: [
      'Illinois uses Sandata for EVV',
      'Required for all Medicaid personal care services',
      'Non-compliance can result in payment denials',
      'Contact HFS EVV helpdesk: (877) 782-5565',
    ],
  },

  {
    id: 'IL-G3',
    itemId: 'G3',
    stateCode: 'IL',
    title: 'Audit-Ready Documentation',
    description: 'Personnel files organized for IDPH surveyor review',
    severity: 'medium',
    regulationCode: '245.260',
    regulationName: 'Records',
    agencyName: 'Illinois Department of Public Health',
    agencyAbbr: 'IDPH',
    agencyPhone: '(217) 782-2913',
    agencyUrl: 'https://dph.illinois.gov',
    questions: [
      {
        id: 'q1',
        question: 'Are your patient and personnel files organized and accessible for IDPH inspection?',
        inputType: 'select',
        options: [
          { value: 'organized', label: 'Yes, all files are organized with consistent structure' },
          { value: 'mostly', label: 'Most files are organized, some need work' },
          { value: 'disorganized', label: 'Files are not well organized' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Could you produce any patient\'s or employee\'s complete file within 5 minutes if IDPH arrived for an unannounced inspection?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'organized' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'mostly' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'disorganized', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Files are well-organized and ready for IDPH inspection.',
      atRisk: 'File organization needs improvement. IDPH surveyors expect immediate access to complete files.',
      nonCompliant: 'Disorganized files will result in survey deficiencies. Implement a standardized file structure immediately.',
      unknown: 'Review file organization against IDPH survey requirements.',
    },
    stateNotes: [
      '77 Ill. Adm. Code 245.260 requires organized and accessible records',
      '210 ILCS 55 authorizes IDPH to conduct unannounced inspections',
      'Files must be retrievable within minutes during IDPH surveys',
      'Standardized file structure recommended for consistent compliance',
    ],
  },
];

export default ILLINOIS_QUESTIONS;